import type { GetStaticPaths } from 'next';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { ContactForm } from '../../../../front/src/contributor/ContactForm/ContactForm';
import { LayoutBlocks } from '../../../../front/src/contributor/Layout/AreaBlocks';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import type { PageType } from '../../../../front/src/types/layout';
import { NoSSR } from '../../../../front/src/utils/ssr';
import { MetaPage } from '../../components/Meta/MetaPage';
import type { GetStaticPropsGD } from '../../utils/getStaticPropsCommon';
import {
  defaultStaticPathsNoPreRender,
  defaultStaticPropsForLayout,
} from '../../utils/getStaticPropsCommon';

const messages = defineMessages({
  title: { defaultMessage: 'Contact - {websiteName}', id: 'N61F1u' },
  heading: { defaultMessage: 'Contact us', id: 'WnQ/AZ' },
  intro: {
    defaultMessage:
      'Would you like to join the team and write articles, suggest a topic to explore, or reach the editorial staff? Write to us. Contributing is on a volunteer basis: this media has no revenue and does not monetize through advertising.',
    id: '4NPFho',
  },
});

// Page publique `/contact` (B7_08) — formulaire de contact universel (tous tenants). Route
// dédiée : un bloc `static-page` ne conviendrait pas (HTML statique, pas de JS interactif) et
// `contact` est réservé côté API (`RESERVED_SLUGS`) pour éviter la collision avec cette route.
// Indexable (index,follow + title + canonical) : la page est un point d'entrée SEO légitime.
const Contact: PageType = props => {
  const { contentAreaLayout } = props;
  const { formatMessage } = useIntl();
  const { name: websiteName } = useWebsite();

  return (
    <>
      <MetaPage
        title={formatMessage(messages.title, { websiteName })}
        index
        follow
        canonicalPath="/contact"
      />
      <div className="contact max-w-[685px] mx-auto flex flex-col gap-y-6">
        {contentAreaLayout && <LayoutBlocks layoutContent={contentAreaLayout} />}
        <h1 className="text-center text-md md:text-xl">
          <FormattedMessage {...messages.heading} />
        </h1>
        <p className="text-center">
          <FormattedMessage {...messages.intro} />
        </p>
        {/* Le formulaire est interactif (état, soumission REST) → rendu client only. Le SEO
            (MetaPage, titre, intro) reste SSR pour les crawlers. */}
        <NoSSR>
          <ContactForm />
        </NoSSR>
      </div>
    </>
  );
};

export const getStaticProps: GetStaticPropsGD = defaultStaticPropsForLayout(Contact);
export const getStaticPaths: GetStaticPaths = defaultStaticPathsNoPreRender;

Contact.pageName = () => 'contact';

export default Contact;
